import { useRef, useState } from 'react';
import { ChevronDown, ChevronRight, Plus, Trash2, Upload } from 'lucide-react';
import {
  DONATION_AMOUNT_MAX,
  DONATION_AMOUNT_MAX_COUNT,
  DONATION_AMOUNT_MIN,
  DONATION_HEADER_MAX_LENGTH,
  DONATION_LOGO_ACCEPT_ATTR,
  donationAmountsError,
  donationLogoError,
  normalizeDonationHeader,
  resolveDonationAmounts,
} from '@signalvote/shared';
import {
  useDonationConfig,
  useSaveDonationConfig,
  useUploadDonationLogo,
} from '@/hooks/queries/useStripeConnectPayments';
import { DonationAmountButtons, DonationHeaderMessage } from '@/components/donations';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

// A blank editor row parses to NaN so donationAmountsError flags it as not-a-whole-dollar (rather than
// Number('') === 0 slipping through as a valid $0).
function parseRow(value: string): number {
  return value.trim() === '' ? Number.NaN : Number(value);
}

/**
 * What to show beside the logo picker. A file the admin just chose is named exactly as they know it; a
 * logo seeded from the saved config only carries a storage key, so fall back to that key's last segment
 * (the sanitized upload name). Neither means nothing is attached yet.
 */
function logoFileLabel(pickedName: string | null, storageKey: string | null): string {
  return pickedName ?? storageKey?.split('/').pop() ?? 'No file chosen';
}

/**
 * The collapsible "Advanced settings" section beside the QR code and embed snippet: the custom
 * suggested-amount editor (1–8 whole-dollar buttons, $1–$50,000, no duplicates) and the optional
 * top-of-form header message (plain text, ≤500 chars), with a live preview that reuses the donor form's
 * own header + amount rendering so the admin sees exactly what donors will. Collapsed by default — a
 * campaign that never opens it keeps exactly the default donation experience. The logo ticket extends it.
 */
export function AdvancedDonationSettings() {
  const [open, setOpen] = useState(false);
  const { data: config } = useDonationConfig();
  const { mutateAsync: saveConfig, isPending } = useSaveDonationConfig();
  const { mutateAsync: uploadLogo, isPending: isUploading } = useUploadDonationLogo();

  // Seed the editor from the saved config (defaults until it loads, and when none are set — so the admin
  // edits the current amounts rather than an empty box). Null means "not yet edited": once the admin
  // touches a row we track their edits and never clobber them on a background refetch.
  const [rows, setRows] = useState<string[] | null>(null);
  // Same "null = not yet edited" seeding for the header message (empty string once the admin clears it).
  const [header, setHeader] = useState<string | null>(null);
  // Same "null = not yet edited" seeding for the logo: once the admin uploads or removes it we track the
  // chosen key + its preview URL, and never clobber that on a background refetch.
  const [logo, setLogo] = useState<{ key: string | null; url: string | null } | null>(null);
  const [logoError, setLogoError] = useState<string | null>(null);
  // The name of the file the admin just chose, so the picker can echo it back (the saved config only
  // carries a storage key). Null until they pick one, and cleared when they remove the logo.
  const [logoName, setLogoName] = useState<string | null>(null);
  // The hidden native input is the only thing that can open the OS file picker; the visible button
  // drives it through this ref.
  const logoInputRef = useRef<HTMLInputElement>(null);
  const [previewSelected, setPreviewSelected] = useState('');
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const effectiveRows = rows ?? resolveDonationAmounts(config?.customAmounts).map(String);
  const effectiveHeader = header ?? config?.headerMessage ?? '';
  const effectiveLogo = logo ?? { key: config?.logoKey ?? null, url: config?.logoUrl ?? null };

  const parsed = effectiveRows.map(parseRow);
  const error = donationAmountsError(parsed);
  // Live preview: mirror the finite values typed so the admin sees the buttons update as they edit;
  // fall back to the defaults when the set is empty (never button-less), exactly like the donor form.
  const previewAmounts = resolveDonationAmounts(parsed.filter((n) => Number.isFinite(n)));
  const selected = previewSelected || String(previewAmounts[0] ?? '');

  const canAdd = effectiveRows.length < DONATION_AMOUNT_MAX_COUNT;
  const canRemove = effectiveRows.length > 1;

  const touched = () => {
    setSaved(false);
    setSaveError(null);
  };
  const edit = (next: string[]) => {
    setRows(next);
    touched();
  };
  const updateRow = (i: number, value: string) =>
    edit(effectiveRows.map((r, idx) => (idx === i ? value : r)));
  const addRow = () => canAdd && edit([...effectiveRows, '']);
  const removeRow = (i: number) => canRemove && edit(effectiveRows.filter((_, idx) => idx !== i));
  const editHeader = (value: string) => {
    setHeader(value);
    touched();
  };

  const handleLogoFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // reset so re-picking the same file fires change again
    if (!file) return;

    setLogoError(null);
    // The server bounds, for immediate feedback; the mint endpoint re-validates authoritatively.
    const clientError = donationLogoError({ type: file.type, size: file.size });
    if (clientError) {
      setLogoError(clientError);
      return;
    }

    try {
      const uploaded = await uploadLogo(file);
      setLogo({ key: uploaded.storageKey, url: uploaded.publicUrl });
      setLogoName(file.name);
      touched();
    } catch (err) {
      setLogoError(err instanceof Error ? err.message : 'Could not upload your logo');
    }
  };

  const removeLogo = () => {
    setLogo({ key: null, url: null });
    setLogoName(null);
    setLogoError(null);
    touched();
  };

  const handleSave = async () => {
    setSaveError(null);
    try {
      await saveConfig({
        customAmounts: parsed,
        headerMessage: normalizeDonationHeader(effectiveHeader),
        logoKey: effectiveLogo.key,
      });
      setSaved(true);
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : 'Could not save your donation settings');
    }
  };

  return (
    <div className="border-t pt-3" data-testid="advanced-donation-settings">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="inline-flex items-center gap-1 text-sm font-medium text-foreground"
      >
        {open ? (
          <ChevronDown aria-hidden="true" className="h-4 w-4" />
        ) : (
          <ChevronRight aria-hidden="true" className="h-4 w-4" />
        )}
        Advanced settings
      </button>

      {open && (
        <div className="mt-3 space-y-4">
          <div className="space-y-2">
            <Label className="text-sm">Suggested donation amounts</Label>
            <p className="text-xs text-muted-foreground">
              Replace the default gift buttons with your own — 1 to {DONATION_AMOUNT_MAX_COUNT}{' '}
              whole-dollar amounts. Donors can still enter any other amount.
            </p>

            <div className="space-y-2">
              {effectiveRows.map((row, i) => (
                <div key={i} className="flex items-center gap-2">
                  <div className="donation-money-input flex max-w-[10rem] items-center rounded-md border px-2">
                    <span aria-hidden="true" className="text-muted-foreground">
                      $
                    </span>
                    <Input
                      type="number"
                      min={DONATION_AMOUNT_MIN}
                      max={DONATION_AMOUNT_MAX}
                      step="1"
                      inputMode="numeric"
                      aria-label={`Amount ${i + 1}`}
                      value={row}
                      onChange={(e) => updateRow(i, e.target.value)}
                      className="border-0 shadow-none focus-visible:ring-0"
                    />
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    aria-label={`Remove amount ${i + 1}`}
                    onClick={() => removeRow(i)}
                    disabled={!canRemove}
                  >
                    <Trash2 aria-hidden="true" className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>

            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={addRow}
              disabled={!canAdd}
            >
              <Plus aria-hidden="true" className="mr-1 h-4 w-4" />
              Add amount
            </Button>

            {error && (
              <p role="alert" className="text-xs text-red-600">
                {error}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="donation-header-message" className="text-sm">
              Header message
            </Label>
            <p className="text-xs text-muted-foreground">
              An optional personal note shown at the top of your donation page. Plain text only.
            </p>
            <textarea
              id="donation-header-message"
              value={effectiveHeader}
              onChange={(e) => editHeader(e.target.value)}
              maxLength={DONATION_HEADER_MAX_LENGTH}
              rows={3}
              className="flex min-h-[4.5rem] w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            />
            <p className="text-right text-xs text-muted-foreground">
              {effectiveHeader.length}/{DONATION_HEADER_MAX_LENGTH}
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="donation-logo-file" className="text-sm">
              Campaign logo
            </Label>
            <p className="text-xs text-muted-foreground">
              A PNG, JPG, or WebP image up to 2 MB, shown at the top of your donation page.
            </p>
            {effectiveLogo.url && (
              <img
                src={effectiveLogo.url}
                alt="Campaign logo preview"
                className="max-h-16 w-auto rounded border object-contain p-1"
              />
            )}
            <div className="flex flex-wrap items-center gap-2">
              {/*
                The browser's own file control renders as unstyled text ("Choose File — No file chosen")
                that reads as prose rather than something clickable, and it can't be themed. Hide it and
                drive it from one of our buttons, so the picker matches every other control on the page
                and we get to name the chosen file ourselves. `hidden` (not a display utility) keeps it
                out of the a11y tree, leaving the button as the single control.
              */}
              <input
                ref={logoInputRef}
                id="donation-logo-file"
                type="file"
                accept={DONATION_LOGO_ACCEPT_ATTR}
                onChange={handleLogoFile}
                disabled={isUploading}
                hidden
              />
              <Button
                type="button"
                data-testid="donation-logo-picker"
                variant="outline"
                size="sm"
                onClick={() => logoInputRef.current?.click()}
                disabled={isUploading}
              >
                <Upload aria-hidden="true" className="h-4 w-4" />
                {isUploading ? 'Uploading…' : effectiveLogo.url ? 'Replace logo' : 'Upload logo'}
              </Button>
              <span
                data-testid="donation-logo-filename"
                className="min-w-0 max-w-[14rem] truncate text-xs text-muted-foreground"
              >
                {logoFileLabel(logoName, effectiveLogo.key)}
              </span>
              {effectiveLogo.url && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={removeLogo}
                  disabled={isUploading}
                >
                  Remove logo
                </Button>
              )}
            </div>
            {logoError && (
              <p role="alert" className="text-xs text-red-600">
                {logoError}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label className="text-sm">Live preview</Label>
            <div className="max-w-sm space-y-3 rounded-md border bg-muted/20 p-3">
              {effectiveLogo.url && (
                <img
                  data-testid="donation-logo"
                  src={effectiveLogo.url}
                  alt="Donation form logo preview"
                  className="mx-auto max-h-16 w-auto object-contain"
                />
              )}
              <DonationHeaderMessage
                message={effectiveHeader}
                className="text-sm leading-relaxed text-foreground"
              />
              <DonationAmountButtons
                amounts={previewAmounts}
                selected={selected}
                onSelect={setPreviewSelected}
                ariaLabel="Donation form preview"
              />
            </div>
          </div>

          {saveError && (
            <div className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700">
              {saveError}
            </div>
          )}

          <div className="flex items-center gap-2">
            <Button type="button" onClick={handleSave} disabled={isPending || !!error}>
              {isPending ? 'Saving…' : 'Save settings'}
            </Button>
            {saved && !error && (
              <span className="text-sm text-muted-foreground">Saved</span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
